/**
 * usePermissions Hook
 * 
 * Fetches and caches user permissions for the active organization.
 * Provides helper functions for permission checks.
 * 
 * Usage:
 * ```tsx
 * const { hasPermission, hasAnyPermission, permissions, isLoading } = usePermissions();
 * 
 * if (hasPermission(PERMISSIONS.MANAGE_USERS)) {
 *   // Show admin UI
 * }
 * ```
 */

import { useQuery } from '@tanstack/react-query';
import { useOrgStore } from '@/stores/orgStore';
import { useAuthStore } from '@/stores/authStore';
import { apiFetch } from '@/lib/api';
import { Permission } from '@/constants/permissions';
import { useMemo } from 'react';

/**
 * User permissions response from API
 */
export interface UserPermissions {
  permissions: string[];
}

/**
 * Permission check functions
 */
export interface PermissionHelpers {
  permissions: string[];
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  isLoading: boolean;
}

/**
 * Query key for permissions
 */
export const permissionsQueryKey = (orgId: string) => ['permissions', 'me', orgId] as const;

/**
 * Hook to fetch and check user permissions
 */
export function usePermissions(): PermissionHelpers {
  const { activeOrgId } = useOrgStore();
  const user = useAuthStore((s) => s.user);

  // Fetch permissions for current org
  // CRITICAL: Only fetch if user is authenticated AND has an active org
  // This prevents infinite loops when unauthenticated users hit protected routes
  const { data, isLoading } = useQuery({
    queryKey: permissionsQueryKey(activeOrgId || ''),
    queryFn: async () => {
      const response = await apiFetch('/api/permissions/me');
      if (!response.ok) {
        throw new Error(`Failed to fetch permissions (${response.status})`);
      }
      const json = await response.json();
      return json.data as UserPermissions;
    },
    enabled: !!activeOrgId && !!user,
    staleTime: 60_000, // Cache for 1 minute
    gcTime: 5 * 60_000, // Keep in cache for 5 minutes
  });

  const permissions = data?.permissions || [];

  // Memoize helper functions
  const hasPermission = useMemo(
    () => (permission: string) => {
      return permissions.includes(permission);
    },
    [permissions]
  );

  const hasAnyPermission = useMemo(
    () => (requiredPermissions: string[]) => {
      return requiredPermissions.some((p) => permissions.includes(p));
    },
    [permissions]
  );

  const hasAllPermissions = useMemo(
    () => (requiredPermissions: string[]) => {
      return requiredPermissions.every((p) => permissions.includes(p));
    },
    [permissions]
  );

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isLoading,
  };
}

